import {
  COMPANY_ADDRESS,
  COMPANY_EMAIL,
  COMPANY_LEGAL_NAME,
  COMPANY_PHONE_DISPLAY,
  COMPANY_PHONE_E164,
  COMPANY_SITE_URL,
} from "@/lib/brand/organization";
import type { ResearchIconName } from "@/lib/icons/research-icons";
import { parseLandingIcon } from "@/lib/landing/puck/icon-options";
import {
  asLandingTextValue,
  styledTextString,
  type LandingTextValue,
} from "@/lib/landing/puck/styled-text";

export type LandingFooterItemKind = "link" | "text";

export type LandingFooterColumnItem = {
  kind: LandingFooterItemKind;
  label: LandingTextValue;
  href?: string;
  icon?: ResearchIconName;
  muted?: boolean;
  external?: boolean;
};

export type LandingFooterColumn = {
  id: string;
  title: LandingTextValue;
  items: LandingFooterColumnItem[];
};

export type LegacyFooterLink = {
  href: string;
  label: LandingTextValue;
  group?: string;
};

export const MAX_FOOTER_COLUMNS = 4;
export const MIN_FOOTER_COLUMNS = 1;
export const MAX_FOOTER_ITEMS_PER_COLUMN = 12;

export function newFooterColumnId(): string {
  return `col-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function inferLegacyGroup(href: string): string {
  const path = href.toLowerCase();
  if (
    path.includes("/legal") ||
    path.includes("privacy") ||
    path.includes("cookies") ||
    path.includes("offer") ||
    path.includes("newsletter")
  ) {
    return "legal";
  }
  return "product";
}

function inferLinkIcon(href: string, group: string): ResearchIconName {
  const path = href.toLowerCase();
  if (group === "product") {
    if (path.includes("pricing") || path.includes("tarif")) return "ticketPercent";
    if (path.includes("cases")) return "briefcase";
    return "arrowRight";
  }
  if (path.includes("offer")) return "receipt";
  if (path.includes("privacy")) return "shieldCheck";
  if (path.includes("cookies")) return "fileText";
  if (path.includes("newsletter")) return "mail";
  return "fileText";
}

function normalizeItem(raw: unknown): LandingFooterColumnItem | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const kind = row.kind === "text" ? "text" : "link";
  const label = asLandingTextValue(row.label);
  if (!styledTextString(label) && kind === "text") return null;
  if (kind === "link" && !styledTextString(label)) return null;
  return {
    kind,
    label,
    href: typeof row.href === "string" ? row.href : kind === "link" ? "#" : undefined,
    icon: parseLandingIcon(row.icon),
    muted: row.muted === true || row.muted === "true",
    external: row.external === true || row.external === "true",
  };
}

function normalizeColumn(raw: unknown): LandingFooterColumn | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const id = typeof row.id === "string" && row.id.trim() ? row.id.trim() : newFooterColumnId();
  const title = asLandingTextValue(row.title);
  const items = Array.isArray(row.items)
    ? row.items.map(normalizeItem).filter((item): item is LandingFooterColumnItem => Boolean(item))
    : [];
  return { id, title, items };
}

export function createDefaultContactsColumn(): LandingFooterColumn {
  return {
    id: "contacts",
    title: "Контакты",
    items: [
      { kind: "text", label: COMPANY_LEGAL_NAME, icon: "building" },
      { kind: "text", label: COMPANY_ADDRESS.formatted, icon: "mapPin", muted: true },
      {
        kind: "link",
        label: COMPANY_PHONE_DISPLAY,
        href: `tel:${COMPANY_PHONE_E164}`,
        icon: "phone",
      },
      {
        kind: "link",
        label: COMPANY_EMAIL,
        href: `mailto:${COMPANY_EMAIL}`,
        icon: "mail",
      },
      {
        kind: "link",
        label: "smartbiznes.org",
        href: COMPANY_SITE_URL,
        icon: "link",
        external: true,
      },
      {
        kind: "link",
        label: "О компании и контакты",
        href: "/legal/contacts",
        icon: "fileText",
      },
    ],
  };
}

export function createDefaultFooterColumns(
  legacyLinks: LegacyFooterLink[] = [],
): LandingFooterColumn[] {
  const product: LandingFooterColumnItem[] = [];
  const legal: LandingFooterColumnItem[] = [];

  for (const link of legacyLinks) {
    const group = (link.group?.trim() || inferLegacyGroup(link.href)).toLowerCase();
    if (group === "account") continue;
    const path = link.href.split("?")[0]?.toLowerCase() ?? "";
    if (["/login", "/register", "/settings", "/dashboard"].includes(path)) continue;
    const item: LandingFooterColumnItem = {
      kind: "link",
      label: link.label,
      href: link.href,
      icon: inferLinkIcon(link.href, group),
    };
    if (group === "legal") legal.push(item);
    else product.push(item);
  }

  return [
    {
      id: "product",
      title: "Продукт",
      items: product.length > 0 ? product : [{ kind: "link", label: "Тарифы", href: "/#pricing", icon: "ticketPercent" }],
    },
    {
      id: "legal",
      title: "Юридическая информация",
      items:
        legal.length > 0
          ? legal
          : [
              { kind: "link", label: "Договор-оферта", href: "/legal/offer", icon: "receipt" },
              {
                kind: "link",
                label: "Согласие на обработку персональных данных",
                href: "/legal/privacy",
                icon: "shieldCheck",
              },
              { kind: "link", label: "Политика Cookies", href: "/legal/cookies", icon: "fileText" },
              {
                kind: "link",
                label: "Согласие на рассылку",
                href: "/legal/newsletter",
                icon: "mail",
              },
            ],
    },
    createDefaultContactsColumn(),
  ];
}

export function normalizeFooterColumns(props: {
  columns?: unknown;
  links?: LegacyFooterLink[];
}): LandingFooterColumn[] {
  if (Array.isArray(props.columns) && props.columns.length > 0) {
    const cols = props.columns
      .map(normalizeColumn)
      .filter((col): col is LandingFooterColumn => Boolean(col))
      .slice(0, MAX_FOOTER_COLUMNS);
    if (cols.length > 0) return cols;
  }
  return createDefaultFooterColumns(props.links);
}

export function serializeFooterColumns(columns: LandingFooterColumn[]) {
  return columns.map((col) => ({
    id: col.id,
    title: col.title,
    items: col.items.map((item) => ({
      kind: item.kind,
      label: item.label,
      href: item.href,
      icon: item.icon,
      muted: item.muted === true ? true : undefined,
      external: item.external === true ? true : undefined,
    })),
  }));
}
